import { FC, useMemo } from 'react';
import {
  Button,
  ChipFilterWithContainer,
  FilterOptions,
  HeaderContent,
  List,
  Spacing,
  StudentExamData,
  StyledDiv,
  TitleText,
  Typography,
} from '@taleemabad/dsm/components';
import { analyticsService, EventNames, ISchoolClassInfo } from '@taleemabad/db';
import { toPascalCase, usePrint } from '@taleemabad/shared';
import { ChipFilterContainer } from '../../shared/shared';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

interface ReportCardResultsTabProps {
  classDetails: ISchoolClassInfo;
  checkedExamsData?: StudentExamData[];
  noResultsText?: string | null;
  assessmentName: string;
  selectedFilter: FilterOptions;
  onFilterChange: (filter: FilterOptions) => void;
  onStudentClick: (examData: StudentExamData) => void;
}

const filters = Object.values(FilterOptions);

export const ReportCardResultsTab: FC<ReportCardResultsTabProps> = ({
  classDetails,
  checkedExamsData,
  noResultsText,
  assessmentName,
  selectedFilter,
  onFilterChange,
  onStudentClick,
}) => {
  const { contentRef, handlePrint } = usePrint({});

  const chipFilters = useMemo(
    () =>
      filters.map((filter) => (
        <ChipFilterWithContainer
          key={filter}
          label={filter}
          onClick={() => onFilterChange(filter)}
          selected={selectedFilter === filter}
        />
      )),
    [selectedFilter, onFilterChange],
  );

  /**
   * retrieves filtered and sorted exam data based on the selected filter.
   *
   * 1. Filters out exams where the student's position or percentage is zero.
   * 2. Sorts the remaining data in descending order by percentage.
   * 3. Extracts unique percentages to account for students with tied scores.
   * 4. Depending on the selected filter:
   *    - If the "Top 3" filter is selected, returns all students within the top three unique percentages.
   *    - If the "Bottom 3" filter is selected, returns all students within the bottom three unique percentages.
   *    - If no specific filter is selected, returns the sorted data as is.
   *
   * This ensures that all students who tie within the top or bottom categories are included,
   * providing a fair representation in cases of identical scores.
   */

  const filteredData = useMemo(() => {
    if (!checkedExamsData) return [];

    const sortByPercentage = (data: StudentExamData[]) => {
      const sortedData = [...data];
      sortedData.sort((a, b) => b.percentage - a.percentage);
      return sortedData;
    };

    const getUniquePercentages = (data: StudentExamData[]) => [
      ...new Set(data.map((item) => item.percentage)),
    ];

    const sortedData = sortByPercentage(checkedExamsData);
    const uniquePercentages = getUniquePercentages(sortedData);

    if (selectedFilter === FilterOptions.TopThreeStudents) {
      const topThreePercentages = uniquePercentages.slice(0, 3);
      return sortedData.filter((item) =>
        topThreePercentages.includes(item.percentage),
      );
    } else if (selectedFilter === FilterOptions.BottomThreeStudents) {
      const bottomThreePercentages = uniquePercentages.slice(-3);
      return sortedData.filter((item) =>
        bottomThreePercentages.includes(item.percentage),
      );
    } else {
      return sortedData;
    }
  }, [checkedExamsData, selectedFilter]);

  const handlePrintClick = () => {
    analyticsService.trackEvent(EventNames.reportCardPrintClicked, {
      epScreenName: 'Class Result',
    });
    handlePrint();
  };

  return (
    <>
      <Spacing marginTop="xl" marginBottom="sm">
        <StyledDiv flexDirection="row-reverse">
          <Button
            icon="download"
            variant="text"
            onClick={handlePrintClick}
            iconColor="--md-ref-primary-50"
          >
            Download & Print
          </Button>
        </StyledDiv>
        <Typography variant="textTitleLarge" fullWidth>
          {assessmentName} Results
        </Typography>
      </Spacing>
      <TitleText
        leftText={`${classDetails.gradeLabel} - ${classDetails.section}`}
        rightText={classDetails.subjectLabel ?? ''}
        seperatorIconName="circle"
      />
      <Spacing margin="lg none">
        <Typography variant="textTitleSmall">
          Select a student to see their subject marks
        </Typography>
      </Spacing>
      <ChipFilterContainer>{chipFilters}</ChipFilterContainer>
      <PrintContentContainer ref={contentRef}>
        <PrintOnlyTypographyContainer>
          <Typography variant="textBodyMediumLightOnSurface">
            {classDetails.gradeLabel} - {classDetails.section}
          </Typography>
        </PrintOnlyTypographyContainer>
        {noResultsText ? (
          <StyledDiv
            justifyContent="center"
            alignItem="center"
            height="calc(100vh - 300px)"
            textAlign="center"
          >
            <Typography variant="textBodyLargeOnSurfaceVariant">
              {noResultsText}
            </Typography>
          </StyledDiv>
        ) : (
          <>
            {filteredData.length > 0 && (
              <StyledTypographyContainer>
                <GroupContainer>
                  <HeaderContent />
                </GroupContainer>
                <GroupContainerDesktop>
                  <HeaderContent />
                </GroupContainerDesktop>
              </StyledTypographyContainer>
            )}
            <SchoolClassContainer>
              {filteredData.map((item) => (
                <List
                  key={item.examId}
                  alignment="center"
                  heading={toPascalCase(item.name)}
                  leadingIcon={{
                    direction: 'leading',
                    source: 'person_outline',
                    name: 'person_outline',
                    type: 'icon',
                  }}
                  trailingIcon={{
                    direction: 'trailing',
                    source: 'keyboard_arrow_right',
                    name: 'keyboard_arrow_right',
                    type: 'icon',
                  }}
                  trailingSupportingText={
                    item.obtainedMarks
                      ? `${item.obtainedMarks}/${item.totalMarks}`
                      : '--'
                  }
                  onClick={() => onStudentClick(item)}
                />
              ))}
            </SchoolClassContainer>
          </>
        )}
      </PrintContentContainer>
    </>
  );
};

const PrintOnlyTypographyContainer = styled.div`
  display: none;
  @media print {
    display: block;
  }
`;

const StyledTypographyContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: var(--rk-spacing-xl);
  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: repeat(1, 1fr);
  }
  margin-top: var(--rk-spacing-xl);
`;

const SchoolClassContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--rk-spacing-xl);
  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: 1fr;
    row-gap: var(--rk-spacing-md);
  }
  margin-top: var(--rk-spacing-md);
`;

const GroupContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 var(--rk-spacing-xxxxxxl) 0 var(--rk-spacing-xl);
  gap: var(--rk-spacing-xl);
`;

const GroupContainerDesktop = styled.div`
  display: flex;
  justify-content: space-between;
  ${mq(BreakpointSizes.xs)} {
    display: none;
  }
`;

const PrintContentContainer = styled.div`
  @media print {
    @page {
      margin: var(--rk-spacing-xxxxxxxxxxxl);
    }
  }
`;
